import React from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { RootState } from "../../../../app/store";
import { Link, useSearchParams } from "react-router-dom";
import { searchParamToObject } from "../../../../helpers/SearchParamHelper";
import { set } from "../../../../app/globalSlice";
import { Chip, Container } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { getLocaleDataGrid } from "../../../../helpers/GetLanguage";
import { GroupageInterface } from "../../../../interfaces/GroupageInterface";
import { objectToQuery, requestApi } from "../../../../helpers/RequestApi";
import { GROUPAGE_URL } from "../../../../utils/UrlsUtils";
import { GET } from "../../../../utils/MethodUtils";
import { toastr } from "react-redux-toastr";
import { FilterFilterInterface } from "../../../../interfaces/FilterInterface";
import { GridSortModel } from "@mui/x-data-grid/models/gridSortModel";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GROUPAGE_PAGE } from "../../../../utils/RouteUtils";
import { LoadingButton } from "@mui/lab";

const GroupagesComponent: React.FC = React.memo(() => {
  const { t, i18n } = useTranslation();
  const refreshPage = useAppSelector(
    (state: RootState) => state.globalState.refreshPage
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const [init, setInit] = React.useState(false);
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const user = useAppSelector((state: RootState) => state.globalState.user);
  const [defaultItemsPerPage] = React.useState(50);
  const [groupages, setGroupages] = React.useState<
    GroupageInterface[] | undefined
  >(undefined);
  const [totalItems, setTotalItems] = React.useState(0);
  const getColumns = React.useCallback((): GridColDef[] => {
    return [
      {
        field: "id",
        headerName: t("column.id"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
      },
      {
        field: "name",
        headerName: t("word.name"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        minWidth: 150,
      },
      {
        field: "gp",
        headerName: t("word.gp"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        minWidth: 150,
      },
      {
        field: "fDocentetes",
        headerName: t("word.fDocentetes"),
        flex: 1,
        headerClassName: "background-nove",
        filterable: false,
        cellClassName: "flex-wrap-wrap",
        renderCell: (params: GridRenderCellParams) => (
          <>
            {params.row.fDocentetes?.map((doPiece: string, index: number) => (
              <Chip key={index} label={doPiece} />
            ))}
          </>
        ),
      },
      {
        field: "actions",
        headerName: t("column.actions"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <>
            <Link
              style={{ textDecoration: "none", color: "inherit" }}
              to={GROUPAGE_PAGE + "/" + params.row.id}
            >
              <IconButton>
                <VisibilityIcon />
              </IconButton>
            </Link>
          </>
        ),
      },
    ];
  }, [t]);
  const [columns] = React.useState<GridColDef[]>(getColumns());

  const load = React.useCallback(
    async (force: boolean = false) => {
      setInit(true);
      if (!user || (groupages !== undefined && !force)) {
        dispatch(set({ refreshPage: false }));
        return;
      }
      const searchParamsObject = searchParamToObject(searchParams);
      let hasChanged = false;
      if (
        !searchParamsObject.hasOwnProperty("page") ||
        searchParamsObject.page === "undefined"
      ) {
        searchParamsObject.page = 1;
        hasChanged = true;
      }
      if (!searchParamsObject.hasOwnProperty("itemsPerPage")) {
        searchParamsObject.itemsPerPage = defaultItemsPerPage;
        hasChanged = true;
      }
      if (hasChanged) {
        dispatch(set({ refreshPage: false }));
        setSearchParams(searchParamsObject, {
          replace: true,
        });
        return;
      }
      const response = await requestApi({
        method: GET,
        path: GROUPAGE_URL + objectToQuery(searchParamsObject),
        allowError: false,
        token: token,
        paginate: true,
      });
      if (response.statusCode === 200) {
        let maxId = Math.max(
          ...response.content["hydra:member"].map(
            (o: FilterFilterInterface) => o.id
          )
        );
        const newGroupages = [
          ...Array.from(
            Array(
              Number(searchParamsObject.itemsPerPage) *
                (Number(searchParamsObject.page) - 1)
            )
          ).map(() => {
            maxId++;
            return {
              id: maxId,
            };
          }),
          ...response.content["hydra:member"],
        ];
        setGroupages(newGroupages);
        setTotalItems(response.content["hydra:totalItems"]);
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        toastr.error(t("word.error"), t("error.tryAgain"));
      }
      dispatch(set({ refreshPage: false }));
    },
    [
      defaultItemsPerPage,
      dispatch,
      groupages,
      searchParams,
      setSearchParams,
      t,
      token,
      user,
    ]
  );

  const onSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      if (!init) {
        return;
      }
      const searchParamsObject = searchParamToObject(searchParams);
      for (const [key] of Object.entries(searchParamsObject)) {
        if (key.startsWith("order")) {
          delete searchParamsObject[key];
        }
      }
      for (const sort of model) {
        // @ts-ignore
        searchParamsObject["order[" + sort.field + "]"] = sort.sort;
      }
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const onPageSizeChange = React.useCallback(
    (pageSize: number) => {
      if (!init) {
        return;
      }
      setGroupages(undefined);
      const searchParamsObject = searchParamToObject(searchParams);
      searchParamsObject.itemsPerPage = pageSize;
      searchParamsObject.page = 1;
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const onPageChange = React.useCallback(
    (page: number) => {
      if (!init) {
        return;
      }
      setGroupages(undefined);
      const searchParamsObject = searchParamToObject(searchParams);
      searchParamsObject.page = page + 1;
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const getSortModel = React.useCallback(() => {
    const searchParamsObject = searchParamToObject(searchParams);
    const result: any[] = [];
    for (const [key, value] of Object.entries(searchParamsObject)) {
      if (key.startsWith("order")) {
        let field: string[] | string = key.split("[");
        field = field[1].replace("]", "");
        result.push({
          field: field,
          sort: value,
        });
      }
    }
    return result;
  }, [searchParams]);

  React.useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (init && refreshPage) {
      load(true);
    }
  }, [refreshPage]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (init && !refreshPage) {
      dispatch(set({ refreshPage: true }));
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchParamsObject = searchParamToObject(searchParams);
  return (
    <>
      <Container maxWidth="xl">
        <Link
          style={{ textDecoration: "none", color: "inherit" }}
          to={GROUPAGE_PAGE + "/new"}
        >
          <LoadingButton variant="contained">
            {t("word.addGroupage")}
          </LoadingButton>
        </Link>
        {init && searchParamsObject.itemsPerPage && searchParamsObject.page && (
          // https://mui.com/x/react-data-grid/components/#pagination
          <DataGrid
            initialState={{
              sorting: {
                sortModel: getSortModel(),
              },
            }}
            loading={refreshPage}
            getRowHeight={() => "auto"}
            rows={refreshPage ? [] : groupages ?? []}
            onSortModelChange={onSortModelChange}
            sortingMode="server"
            page={Number(searchParamsObject.page) - 1}
            rowsPerPageOptions={[10, 25, 50]}
            pageSize={Number(searchParamsObject.itemsPerPage)}
            onPageSizeChange={onPageSizeChange}
            onPageChange={onPageChange}
            rowCount={totalItems}
            columns={columns}
            autoHeight={true}
            disableExtendRowFullWidth={true}
            localeText={getLocaleDataGrid(i18n.language)}
          />
        )}
      </Container>
    </>
  );
});

export default GroupagesComponent;
